import $ from "jquery";

import type {MessageList} from "./message_list.ts";
import * as message_lists from "./message_lists.ts";
import type {Message} from "./message_store.ts";
import * as left_sidebar_custom from "./left_sidebar_custom.ts";
import * as rendered_markdown from "./rendered_markdown.ts";

type PersistedBreakpageState = {
    hash: string;
    message_id: number;
    view: "page" | "chat";
    html?: string;
};

const STORAGE_KEY = "zulip:breakpage-state";

let visible = false;
let currentMessage: Message | undefined;
let currentHtml = "";
let currentMessageListId: number | undefined;
let suppressedMessageId: number | undefined;
let suppressedMessageListId: number | undefined;
let suppressedMessage: Message | undefined;
let restoredFromStorage = false;
let persistedState: PersistedBreakpageState | undefined;
type HideOptions = {preserveState?: boolean};

function readPersistedState(): PersistedBreakpageState | undefined {
    try {
        const storage = window.sessionStorage;
        if (storage === undefined) {
            return undefined;
        }
        const raw = storage.getItem(STORAGE_KEY);
        if (raw === null) {
            return undefined;
        }
        const parsed = JSON.parse(raw) as PersistedBreakpageState;
        if (
            typeof parsed !== "object" ||
            parsed === null ||
            typeof parsed.hash !== "string" ||
            typeof parsed.message_id !== "number" ||
            (parsed.view !== "page" && parsed.view !== "chat")
        ) {
            return undefined;
        }
        return parsed;
    } catch {
        return undefined;
    }
}

function writePersistedState(state: PersistedBreakpageState | undefined): void {
    persistedState = state;
    try {
        const storage = window.sessionStorage;
        if (storage === undefined) {
            return;
        }
        if (state === undefined) {
            storage.removeItem(STORAGE_KEY);
        } else {
            storage.setItem(STORAGE_KEY, JSON.stringify(state));
        }
    } catch {
        // Ignore quota/security errors; persistence is best-effort only.
    }
}

function currentHash(): string {
    return window.location.hash;
}

function applyPersistedState(): void {
    persistedState = readPersistedState();
    if (persistedState === undefined) {
        restoredFromStorage = false;
        return;
    }

    if (persistedState.hash !== currentHash()) {
        if (persistedState.view === "page") {
            restoredFromStorage = false;
        }
        if (persistedState.view === "chat") {
            suppressedMessageId = persistedState.message_id;
            suppressedMessageListId = undefined;
        }
        return;
    }

    if (persistedState.view === "chat") {
        suppressedMessageId = persistedState.message_id;
        suppressedMessageListId = undefined;
        restoredFromStorage = false;
        return;
    }

    restoredFromStorage = true;
    $("#message_feed_container").hide();
    $("#compose").hide();
    const $view = $("#breakpage-view");
    $view.removeClass("hidden").show();
    const $container = $("#breakpage-container");
    if (persistedState.html !== undefined) {
        $container.html(persistedState.html);
        rendered_markdown.update_elements($container);
    } else {
        $container.empty();
    }
}

function storePageState(view: "page" | "chat"): void {
    if (currentMessage === undefined) {
        if (persistedState !== undefined && persistedState.hash === currentHash()) {
            if (view === "chat") {
                writePersistedState({
                    hash: currentHash(),
                    message_id: persistedState.message_id,
                    view: "chat",
                });
            }
        }
        return;
    }

    const state: PersistedBreakpageState = {
        hash: currentHash(),
        message_id: currentMessage.id,
        view,
    };

    if (view === "page") {
        state.html = currentHtml;
    }

    writePersistedState(state);
}

function clearPersistedStateIfMatch(): void {
    if (persistedState !== undefined && persistedState.hash === currentHash()) {
        writePersistedState(undefined);
    }
}

function show(message: Message, messageList: MessageList): boolean {
    const previousVisible = visible;
    const previousMessageId = currentMessage?.id;
    const previousHtml = currentHtml;
    const previousListId = currentMessageListId;

    currentMessage = message;
    currentHtml = message.breakpage_html ?? "";
    currentMessageListId = messageList.id;
    suppressedMessageId = undefined;
    suppressedMessageListId = undefined;
    suppressedMessage = undefined;

    $("#message_feed_container").hide();
    $("#compose").hide();
    const $view = $("#breakpage-view");
    $view.removeClass("hidden").show();
    const $container = $("#breakpage-container");
    $container.html(currentHtml);
    rendered_markdown.update_elements($container);
    left_sidebar_custom.update_message_categories(message);

    visible = true;
    restoredFromStorage = false;
    storePageState("page");

    return (
        !previousVisible ||
        previousMessageId !== message.id ||
        previousHtml !== currentHtml ||
        previousListId !== messageList.id
    );
}

function hide({preserveState = false}: HideOptions = {}): boolean {
    if (!visible && currentMessage === undefined && !restoredFromStorage) {
        return false;
    }

    $("#breakpage-container").empty();
    $("#breakpage-view").addClass("hidden").hide();
    $("#message_feed_container").show();
    $("#compose").show();

    visible = false;
    currentMessage = undefined;
    currentHtml = "";
    currentMessageListId = undefined;
    restoredFromStorage = false;
    if (!preserveState) {
        suppressedMessageId = undefined;
        suppressedMessageListId = undefined;
        suppressedMessage = undefined;
        clearPersistedStateIfMatch();
    }
    return true;
}

export function sync_with_message_list(msg_list: MessageList | undefined): boolean {
    if (msg_list === undefined) {
        suppressedMessageId = undefined;
        suppressedMessageListId = undefined;
        suppressedMessage = undefined;
        if (restoredFromStorage) {
            return false;
        }
        return hide();
    }

    const filter = msg_list.data.filter;
    const is_conversation_view =
        filter.is_conversation_view() || filter.is_conversation_view_with_near();

    if (!is_conversation_view) {
        suppressedMessageId = undefined;
        suppressedMessageListId = undefined;
        if (restoredFromStorage) {
            return false;
        }
        return hide();
    }

    if (
        suppressedMessageListId !== undefined &&
        suppressedMessageListId !== msg_list.id
    ) {
        suppressedMessageId = undefined;
        suppressedMessageListId = undefined;
    }

    const message = msg_list
        .data
        .all_messages()
        .find((candidate) => candidate.breakpage_html !== undefined);

    if (message?.breakpage_html !== undefined) {
        currentMessageListId = msg_list.id;

        if (
            persistedState !== undefined &&
            persistedState.hash === currentHash() &&
            persistedState.view === "chat" &&
            persistedState.message_id === message.id
        ) {
            const changed =
                suppressedMessageId !== message.id ||
                suppressedMessageListId !== msg_list.id ||
                suppressedMessage !== message ||
                visible;

            suppressedMessageId = message.id;
            suppressedMessageListId = msg_list.id;
            suppressedMessage = message;
            return changed;
        }

        if (
            suppressedMessageId === message.id &&
            suppressedMessageListId === msg_list.id
        ) {
            suppressedMessage = message;
            return false;
        }

        return show(message, msg_list);
    }

    currentMessageListId = msg_list.id;
    suppressedMessageId = undefined;
    suppressedMessageListId = undefined;
    suppressedMessage = undefined;
    if (restoredFromStorage) {
        return false;
    }
    return hide();
}

export function is_visible(): boolean {
    return visible;
}

export function get_current_message(): Message | undefined {
    return currentMessage;
}

function resolve_message_list(): MessageList | undefined {
    if (suppressedMessageListId !== undefined) {
        const list = message_lists.get_rendered_message_list_by_id(suppressedMessageListId);
        if (list !== undefined) {
            return list;
        }
    }

    return message_lists.current;
}

export function force_chat_view(): boolean {
    if (!visible) {
        return false;
    }

    if (currentMessage === undefined || currentMessageListId === undefined) {
        return hide();
    }

    suppressedMessageId = currentMessage.id;
    suppressedMessageListId = currentMessageListId;
    suppressedMessage = currentMessage;
    storePageState("chat");
    return hide({preserveState: true});
}

export function has_suppressed_message(): boolean {
    return suppressedMessageId !== undefined && suppressedMessage !== undefined;
}

export function get_suppressed_message(): Message | undefined {
    return suppressedMessage;
}

export function restore_page_view(): boolean {
    if (suppressedMessageId === undefined) {
        suppressedMessage = undefined;
        return false;
    }

    const list = resolve_message_list();
    if (list === undefined) {
        return false;
    }

    const message = suppressedMessage ?? list.get(suppressedMessageId);
    if (message === undefined || message.breakpage_html === undefined) {
        suppressedMessageId = undefined;
        suppressedMessageListId = undefined;
        suppressedMessage = undefined;
        return false;
    }

    const changed = show(message, list);
    if (!changed) {
        storePageState("page");
    }
    return changed;
}

export function initialize(): void {
    applyPersistedState();
}

export function show_external_iframe(message: Message | undefined, url: string): void {
    const list = resolve_message_list();
    let usedMessage: Message | undefined;
    if (message !== undefined && list !== undefined) {
        show(message, list);
        usedMessage = message;
    } else {
        $("#message_feed_container").hide();
        $("#compose").hide();
        const $view = $("#breakpage-view");
        $view.removeClass("hidden").show();
        usedMessage = undefined;
        currentMessage = undefined;
        currentHtml = "";
        currentMessageListId = undefined;
        suppressedMessageId = undefined;
        suppressedMessageListId = undefined;
        suppressedMessage = undefined;
        visible = true;
        restoredFromStorage = false;
        if (message !== undefined) {
            left_sidebar_custom.update_message_categories(message);
        }
    }

    const $container = $("#breakpage-container");
    const $iframe = $("<iframe>")
        .addClass("left-sidebar-external-frame")
        .attr("src", url)
        .attr("title", "External content")
        .attr("loading", "lazy");
    $container.empty().append($iframe);
    currentHtml = $container.html() ?? "";

    if (usedMessage !== undefined) {
        storePageState("page");
    } else {
        writePersistedState(undefined);
    }
}
